package com.partejar.app.feature.relacionamentos.presentation

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.semantics
import com.partejar.app.core.format.formatBrazilianDateTime
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonSize
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarMotion
import com.partejar.app.core.ui.components.PartejarStatusBadge
import com.partejar.app.core.ui.components.PartejarStatusBadgeTone
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.partejarPressFeedback
import com.partejar.app.core.ui.components.reducedMotion
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.relacionamentos.data.InteractionKind
import com.partejar.app.feature.relacionamentos.data.RelacionamentoStage
import kotlinx.datetime.LocalDateTime

/** Ordered by how often a conversation happens through each kind. */
internal val conversationKinds = listOf(
    InteractionKind.WHATSAPP,
    InteractionKind.INSTAGRAM,
    InteractionKind.MEETING,
    InteractionKind.FOLLOW_UP,
    InteractionKind.CONTACT,
    InteractionKind.OTHER
)

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
internal fun RegisterConversationSheet(
    name: String,
    kind: InteractionKind,
    note: String,
    nextFollowUp: String?,
    submitting: Boolean,
    error: String?,
    now: LocalDateTime,
    onKindChange: (InteractionKind) -> Unit,
    onNoteChange: (String) -> Unit,
    onNextFollowUpChange: (String?) -> Unit,
    onPickFollowUp: () -> Unit,
    onDismiss: () -> Unit,
    onSave: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val shortcut = selectedFollowUpShortcut(nextFollowUp, now)
    val customDate = nextFollowUp?.takeIf { shortcut == null }
    val errorRequester = remember { BringIntoViewRequester() }
    LaunchedEffect(error) {
        if (error != null) errorRequester.bringIntoView()
    }

    RelationshipSheet(
        title = "Registrar conversa",
        subtitle = "com $name",
        onDismiss = { if (!submitting) onDismiss() }
    ) {
        SheetQuestion("Que tipo de conversa foi?")
        FlowRow(
            modifier = Modifier.fillMaxWidth().selectableGroup(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalArrangement = Arrangement.spacedBy(spacing.space8)
        ) {
            conversationKinds.forEach { option ->
                val visual = interactionPresentation(option)
                RelationshipChoiceChip(
                    label = visual.label,
                    icon = visual.icon,
                    selected = option == kind,
                    enabled = !submitting,
                    onClick = { onKindChange(option) }
                )
            }
        }

        PartejarTextField(
            value = note,
            onValueChange = onNoteChange,
            label = "O que foi conversado? (opcional)",
            placeholder = "Ex.: pediu valores e quer conversar com o parceiro",
            singleLine = false,
            minLines = 3,
            maxLines = 6,
            enabled = !submitting
        )

        SheetQuestion("Quando falar com ela de novo?")
        FlowRow(
            modifier = Modifier.fillMaxWidth().selectableGroup(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalArrangement = Arrangement.spacedBy(spacing.space8)
        ) {
            FollowUpShortcut.entries.forEach { option ->
                RelationshipChoiceChip(
                    label = option.label,
                    selected = option == shortcut,
                    enabled = !submitting,
                    onClick = { onNextFollowUpChange(followUpShortcutValue(option, now)) }
                )
            }
            RelationshipChoiceChip(
                label = customDate?.let { formatBrazilianDateTime(it) ?: it } ?: "Escolher data",
                icon = PartejarIcons.Clinical.calendar,
                selected = customDate != null,
                enabled = !submitting,
                onClick = onPickFollowUp
            )
            RelationshipChoiceChip(
                label = "Não marcar",
                selected = nextFollowUp == null,
                enabled = !submitting,
                onClick = { onNextFollowUpChange(null) }
            )
        }
        Text(
            nextFollowUp?.let { value -> relativeMoment(value, now)?.let { "Retorno $it." } }
                ?: "Sem retorno marcado.",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = MaterialTheme.shapes.medium,
            color = PartejarTheme.colors.infoContainer,
            contentColor = PartejarTheme.colors.onInfoContainer
        ) {
            Text(
                "Registrar uma conversa não muda a etapa. Para avançar, use “Mudar etapa”.",
                modifier = Modifier.padding(spacing.space12),
                style = MaterialTheme.typography.bodySmall
            )
        }

        error?.let {
            PartejarAlert(
                title = "Conversa não salva",
                tone = PartejarAlertTone.ERROR,
                modifier = Modifier.bringIntoViewRequester(errorRequester).semantics { liveRegion = LiveRegionMode.Polite }
            ) {
                Text(it)
                Text("Tente salvar novamente. Seus dados foram mantidos.")
            }
        }

        PartejarButton(
            text = "Salvar conversa",
            onClick = onSave,
            isLoading = submitting,
            shape = PartejarShapes.pill,
            motionEnabled = true
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun RelationshipStageSheet(
    stage: RelacionamentoStage,
    busy: Boolean,
    onDismiss: () -> Unit,
    onMove: (RelacionamentoStage) -> Unit,
    onStartContract: () -> Unit,
    onCloseRequest: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val targets = manualStageTargets(stage)
    var selected by rememberSaveable(stage) { mutableStateOf(targets.firstOrNull()) }

    RelationshipSheet(
        title = "Em que etapa ela está?",
        subtitle = "As etapas só avançam. Escolha a que descreve o momento.",
        onDismiss = onDismiss
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().selectableGroup(),
            verticalArrangement = Arrangement.spacedBy(spacing.space8)
        ) {
            relationshipJourney.forEachIndexed { index, option ->
                when {
                    option == RelacionamentoStage.CONTRACT -> StageOption(
                        number = index + 1,
                        stage = option,
                        state = StageOptionState.CONTRACT
                    ) {
                        PartejarButton(
                            text = "Iniciar contrato",
                            onClick = onStartContract,
                            enabled = !busy,
                            variant = PartejarButtonVariant.SECONDARY,
                            size = PartejarButtonSize.COMPACT,
                            fillWidth = false,
                            shape = PartejarShapes.pill,
                            leadingIcon = {
                                Icon(PartejarIcons.Relationships.contract, null, Modifier.size(PartejarTheme.dimensions.iconSmall))
                            }
                        )
                    }
                    option == stage -> StageOption(index + 1, option, StageOptionState.CURRENT)
                    option in targets -> StageOption(
                        number = index + 1,
                        stage = option,
                        state = if (option == selected) StageOptionState.SELECTED else StageOptionState.AVAILABLE,
                        onClick = { selected = option }
                    )
                    else -> StageOption(index + 1, option, StageOptionState.DONE)
                }
            }
        }

        selected?.let { target ->
            PartejarButton(
                text = "Mover para ${stageTitle(target)}",
                onClick = { onMove(target) },
                enabled = !busy,
                isLoading = busy,
                shape = PartejarShapes.pill,
                motionEnabled = true
            )
        }

        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = MaterialTheme.shapes.medium,
            color = MaterialTheme.colorScheme.surfaceContainerLow
        ) {
            Column(
                modifier = Modifier.padding(spacing.space16),
                verticalArrangement = Arrangement.spacedBy(spacing.space12)
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                    Text("Ela não vai seguir?", style = MaterialTheme.typography.titleSmall)
                    Text(
                        "O relacionamento fica salvo só para consulta.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                PartejarButton(
                    text = "Encerrar como perdido",
                    onClick = onCloseRequest,
                    enabled = !busy,
                    variant = PartejarButtonVariant.DESTRUCTIVE_SECONDARY,
                    size = PartejarButtonSize.COMPACT,
                    shape = PartejarShapes.pill,
                    leadingIcon = {
                        Icon(PartejarIcons.Relationships.lost, null, Modifier.size(PartejarTheme.dimensions.iconSmall))
                    }
                )
            }
        }
    }
}

private enum class StageOptionState { DONE, CURRENT, AVAILABLE, SELECTED, CONTRACT }

@Composable
private fun StageOption(
    number: Int,
    stage: RelacionamentoStage,
    state: StageOptionState,
    onClick: (() -> Unit)? = null,
    action: @Composable ColumnScope.() -> Unit = {}
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val selected = state == StageOptionState.SELECTED
    val selectableModifier = if (onClick != null) {
        Modifier.selectable(selected = selected, role = Role.RadioButton, onClick = onClick)
    } else {
        Modifier
    }
    Surface(
        modifier = Modifier.fillMaxWidth().then(selectableModifier),
        shape = MaterialTheme.shapes.medium,
        color = when (state) {
            StageOptionState.SELECTED -> MaterialTheme.colorScheme.primaryContainer
            StageOptionState.CURRENT -> MaterialTheme.colorScheme.surfaceContainerLow
            else -> MaterialTheme.colorScheme.surface
        },
        border = BorderStroke(
            if (selected) dimensions.strokeStrong else dimensions.strokeThin,
            if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant
        )
    ) {
        Row(
            modifier = Modifier.padding(spacing.space16),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.Top
        ) {
            Text(
                "$number",
                style = MaterialTheme.typography.titleSmall,
                color = if (state == StageOptionState.DONE) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.primary
            )
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(spacing.space8),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        stageTitle(stage),
                        style = MaterialTheme.typography.titleSmall,
                        modifier = Modifier.weight(1f, fill = false),
                        color = if (state == StageOptionState.DONE) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onSurface
                    )
                    when (state) {
                        StageOptionState.CURRENT -> PartejarStatusBadge("Atual")
                        StageOptionState.DONE -> PartejarStatusBadge("Concluída", tone = PartejarStatusBadgeTone.SUCCESS)
                        else -> Unit
                    }
                }
                Text(
                    if (stage == RelacionamentoStage.CONTRACT) "Ela decidiu contratar. Essa etapa começa quando você inicia o contrato."
                    else stageGuidance(stage),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                action()
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun RelationshipSheet(
    title: String,
    subtitle: String,
    onDismiss: () -> Unit,
    content: @Composable ColumnScope.() -> Unit
) {
    val spacing = PartejarTheme.spacing
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    ModalBottomSheet(onDismissRequest = onDismiss, sheetState = sheetState) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .navigationBarsPadding()
                .imePadding()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = spacing.space20)
                .padding(bottom = spacing.space24),
            verticalArrangement = Arrangement.spacedBy(spacing.space16)
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(title, style = MaterialTheme.typography.titleLarge, modifier = Modifier.semantics { heading() })
                Text(subtitle, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
            content()
        }
    }
}

@Composable
private fun SheetQuestion(text: String) {
    Text(text, style = MaterialTheme.typography.titleSmall, modifier = Modifier.semantics { heading() })
}

@Composable
internal fun RelationshipChoiceChip(
    label: String,
    selected: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
    icon: ImageVector? = null
) {
    val spacing = PartejarTheme.spacing
    val reduced = reducedMotion()
    val interaction = remember { MutableInteractionSource() }
    val pressed by interaction.collectIsPressedAsState()
    val container by animateColorAsState(
        if (selected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceContainerLow,
        if (reduced) snap() else tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
        label = "relationshipChoiceContainer"
    )
    val content by animateColorAsState(
        if (selected) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurfaceVariant,
        if (reduced) snap() else tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
        label = "relationshipChoiceContent"
    )

    Surface(
        modifier = Modifier
            .heightIn(min = PartejarTheme.dimensions.minimumTouchTarget)
            .partejarPressFeedback(pressed, enabled)
            .selectable(
                selected,
                enabled = enabled,
                interactionSource = interaction,
                indication = null,
                role = Role.RadioButton,
                onClick = onClick
            ),
        shape = PartejarShapes.pill,
        color = container,
        contentColor = content,
        border = BorderStroke(
            if (selected) PartejarTheme.dimensions.strokeStrong else PartejarTheme.dimensions.strokeThin,
            if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant
        )
    ) {
        Row(
            Modifier.padding(horizontal = spacing.space12, vertical = spacing.space8),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(spacing.space8)
        ) {
            icon?.let {
                Icon(it, contentDescription = null, modifier = Modifier.size(PartejarTheme.dimensions.iconSmall))
            }
            Text(label, style = MaterialTheme.typography.labelLarge)
        }
    }
}
